import boardsApi from '@/plugins/graphql/boards'

export default {
  namespaced: true,
  state: {
    boards: [],
    currentBoard: null,
    currentMilestoneBoard: null,
    currentBoardMemberPerformance: null,
    currentBoardTeamPerformance: null,
    currentBoardPerformance: null,
    loading: false,
    processing: false,
    fetchingBoard: false,
    error: null,
    currentPopoverTask: null,
    task: null,
    statusPages: {},
    temporaryAssetToken: null,
    currentComment: null,
  },
  getters: {
    GET_CURRENT_COMMENT: state => state.currentComment,
    GET_BOARDS: state => state.boards,
    GET_CURRENT_BOARD: state => state.currentBoard,
    GET_CURRENT_MILESTONE_BOARD: state => state.currentMilestoneBoard,
    GET_LOADING: state => state.loading,
    GET_PROCESSING: state => state.processing,
    GET_FETCHING_BOARD: state => state.fetchingBoard,
    GET_ERROR: state => state.error,
    GET_BOARD_BY_UUID: state => uuid => state.boards.find(b => b.uuid === uuid),
    GET_CURRENT_BOARD_LABELS: state => state.currentBoard?.labels?.data || [],
    GET_CURRENT_POPOVER_TASK: state => state.currentPopoverTask,
    GET_CURRENT_BOARD_MILESTONES: state => state.currentBoard?.milestones?.data || [],
    GET_TASK: state => state.task,
    GET_STATUS_PAGES: state => state.statusPages,
    GET_TEMPORARY_ASSET_TOKEN: state => state.temporaryAssetToken,
    GET_CURRENT_BOARD_MEMBER_PERFORMANCE: state => state.currentBoardMemberPerformance,
    GET_CURRENT_BOARD_TEAM_PERFORMANCE: state => state.currentBoardTeamPerformance,
    GET_CURRENT_BOARD_PERFORMANCE: state => state.currentBoardPerformance,
  },
  mutations: {
    SET_CURRENT_COMMENT(state, comment) {
      state.currentComment = comment
    },
    SET_CURRENT_BOARD_MEMBER_PERFORMANCE(state, performance) {
      state.currentBoardMemberPerformance = performance
    },
    SET_CURRENT_BOARD_TEAM_PERFORMANCE(state, performance) {
      state.currentBoardTeamPerformance = performance
    },
    SET_CURRENT_BOARD_PERFORMANCE(state, performance) {
      state.currentBoardPerformance = performance
    },
    SET_TEMPORARY_ASSET_TOKEN(state, token) {
      state.temporaryAssetToken = token
    },
    SET_STATUS_PAGES(state, { statusKey, data }) {
      state.statusPages = {
        ...state.statusPages,
        [statusKey]: {
          ...data,
        },
      }
    },
    SET_BOARDS(state, boards) {
      state.boards = boards
    },
    SET_CURRENT_BOARD(state, board) {
      state.currentBoard = board
    },
    SET_CURRENT_MILESTONE_BOARD(state, board) {
      state.currentMilestoneBoard = board
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_PROCESSING(state, processing) {
      state.processing = processing
    },
    SET_FETCHING_BOARD(state, fetching) {
      state.fetchingBoard = fetching
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_CURRENT_POPOVER_TASK(state, taskUid) {
      state.currentPopoverTask = taskUid
    },
    ADD_BOARD(state, board) {
      state.boards.push(board)
    },
    UPDATE_BOARD(state, updatedBoard) {
      const index = state.boards.findIndex(b => b.uuid === updatedBoard.uuid)
      if (index !== -1) {
        state.boards.splice(index, 1, { ...state.boards[index], ...updatedBoard })
      }
      if (state.currentBoard?.uuid === updatedBoard.uuid) {
        state.currentBoard = { ...state.currentBoard, ...updatedBoard }
      }
    },
    REMOVE_BOARD(state, boardUid) {
      state.boards = state.boards.filter(b => b.uuid !== boardUid)
      if (state.currentBoard?.uuid === boardUid) {
        state.currentBoard = null
      }
    },
    ADD_STATUS(state, { boardId, status }) {
      if (state.currentBoard && state.currentBoard.id === boardId) {
        state.currentBoard.statuses = [...(state.currentBoard.statuses || []), status]
      }
    },
    UPDATE_STATUS(state, updatedStatus) {
      if (state.currentBoard?.statuses) {
        const index = state.currentBoard.statuses.findIndex(s => s.id === updatedStatus.id)
        if (index !== -1) {
          state.currentBoard.statuses.splice(index, 1, updatedStatus)
        }
      }
    },
    REMOVE_STATUS(state, statusId) {
      if (state.currentBoard?.statuses) {
        state.currentBoard.statuses = state.currentBoard.statuses.filter(s => s.id !== statusId)
      }
    },
    ADD_BOARD_MEMBER(state, member) {
      if (state.currentBoard) {
        state.currentBoard.members = state.currentBoard.members || { data: [], total: 0 }
        state.currentBoard.members.data.push(member)
        state.currentBoard.members.total = (state.currentBoard.members.total || 0) + 1
      }
    },
    REMOVE_BOARD_MEMBER(state, memberUid) {
      if (state.currentBoard?.members) {
        state.currentBoard.members.data = state.currentBoard.members.data.filter(m => m.uuid !== memberUid)
        state.currentBoard.members.total = Math.max(0, (state.currentBoard.members.total || 1) - 1)
      }
    },
    ADD_TASK(state, task) {
      if (state.currentBoard?.tasks?.data) {
        state.currentBoard.tasks.data.push(task)
        state.currentBoard.tasks.total += 1
      }
    },
    UPDATE_TASK(state, updatedTask) {
      if (state.currentBoard?.tasks?.data) {
        const index = state.currentBoard.tasks.data.findIndex(t => t.uuid === updatedTask.uuid)
        if (index !== -1) {
          state.currentBoard.tasks.data.splice(index, 1, { ...state.currentBoard.tasks.data[index], ...updatedTask })
        }
      }
    },
    REMOVE_TASK(state, taskUuid) {
      if (state.currentBoard?.tasks?.data) {
        state.currentBoard.tasks.data = state.currentBoard.tasks.data.filter(t => t.uuid !== taskUuid)
        state.currentBoard.tasks.total -= 1
      }
    },
    CHANGE_TASK_STATUS(state, { taskUid, currentStatusId, targetStatusId }) {
      if (state.currentBoard?.statuses) {
        const status = state.currentBoard.statuses.find(s => s.id === targetStatusId)
        const prevStatus = state.currentBoard.statuses.find(s => s.id === currentStatusId)

        // Decrease total count from previous status and increase in target status
        if (prevStatus) {
          prevStatus.tasks.total -= 1
        }
        if (status) {
          status.tasks.total += 1
        }

        // Update task's status
        const task = status.tasks?.data.find(t => t.uuid === taskUid)
        if (task && status) {
          // Avoid circular reference by not assigning the full status object with tasks
          task.status = {
            id: status.id,
            key: status.key,
            name: status.name,
            color: status.color,
            order: status.order,
          }
        }
      }
    },
    SET_BOARD_LABELS(state, labels) {
      if (state.currentBoard) {
        state.currentBoard.labels.data = labels
      }
    },

    SET_TASK(state, task) {
      if (task) {
        state.task = task
      }
    },
    CHANGE_STATUS_TOTAL(state, { statusKey, delta }) {
      if (state.currentBoard?.statuses) {
        const status = state.currentBoard.statuses.find(s => s.key === statusKey)
        if (status) {
          status.tasks.total = (status.tasks.total || 0) + delta
        }
      }
    },
  },
  actions: {
    updateCurrentComment({ commit }, comment) {
      commit('SET_CURRENT_COMMENT', comment)
    },
    setCurrentPopoverTask({ commit }, taskUid) {
      commit('SET_CURRENT_POPOVER_TASK', taskUid)
    },
    async fetchBoards({ commit }, projectUid) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await boardsApi.getProjectBoards({ projectUid, first: 50 })
        commit('SET_BOARDS', response.data.boards.data)
        return response.data.boards.data
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchBoard(
      { commit },
      {
        uuid,
        assignees = [],
        labelUids = [],
        statusKeys = [],
        priority = null,
        search = null,
        milestoneUid = null,
      } = {},
    ) {
      commit('SET_FETCHING_BOARD', true)
      commit('SET_ERROR', null)

      try {
        const params = { uuid }

        if (assignees.length) {
          if (assignees[0] === 'unassigned') {
            params.assignees = []
          } else {
            params.assignees = assignees
          }
        }

        if (labelUids.length) params.labelUids = labelUids
        if (statusKeys.length) params.statusKeys = statusKeys
        if (priority) params.priority = priority
        if (milestoneUid) params.milestoneUid = milestoneUid
        if (search) params.search = search

        const { data } = await boardsApi.getBoard(params)
        if (milestoneUid) {
          commit('SET_CURRENT_MILESTONE_BOARD', {
            ...data.board,
            statuses: data.board?.statuses.map(status => ({
              ...status,
              tasks: {
                data: status.tasks?.data.filter(task => task.milestone)
                  .filter(task => task.milestone?.uuid === milestoneUid) || [],
              },
            })),
          })
        } else {
          commit('SET_CURRENT_MILESTONE_BOARD', null)
        }

        if (data.board?.statuses) {
          data.board.statuses.forEach(status => {
            commit('SET_STATUS_PAGES', {
              statusKey: status.key,
              data: { page: 1, hasMorePages: status.tasks.hasMorePages, fetchingMoreTasks: false },
            })
          })
        }

        commit('SET_CURRENT_BOARD', data.board)
        return data.board
      } catch (error) {
        const message = error?.response?.data?.message
          || error?.message
          || 'Failed to fetch board'

        commit('SET_ERROR', message)
        throw error
      } finally {
        commit('SET_FETCHING_BOARD', false)
      }
    },

    clearCurrentBoard({ commit }) {
      commit('SET_CURRENT_BOARD', null)
    },

    async createBoard({ commit }, { projectUid, input }) {
      try {
        const response = await boardsApi.createBoard({ projectUid, input })
        const result = response.data.createBoard
        if (result.status === 'RECORD_INSERTED') {
          commit('ADD_BOARD', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to create board')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async updateBoard({ commit }, { projectUid, boardUid, input }) {
      try {
        const response = await boardsApi.updateBoard({ projectUid, boardUid, input })
        const result = response.data.updateBoard

        if (result.status === 'RECORD_UPDATED') {
          commit('UPDATE_BOARD', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to update board')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async updateBoardOrder({ commit }, { projectUid, boardUid, order }) {
      try {
        const response = await boardsApi.updateBoard({ projectUid, boardUid, input: { order } })
        const result = response.data.updateBoard
        if (result.status === 'RECORD_UPDATED') {
          commit('UPDATE_BOARD', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to update board order')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async deleteBoard({ commit }, { projectUid, boardUid }) {
      try {
        const response = await boardsApi.deleteBoard({ projectUid, boardUid })
        const result = response.data.deleteBoard
        if (result.status === 'RECORD_DELETED') {
          commit('REMOVE_BOARD', boardUid)
          return result
        }
        throw new Error(result.message || 'Failed to delete board')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async createTaskStatus({ commit }, input) {
      try {
        const response = await boardsApi.createTaskStatus(input)
        const result = response.data.createTaskStatus
        if (result.status === 'RECORD_INSERTED') {
          commit('ADD_STATUS', { boardId: input.boardId, status: result.data })
          return result
        }
        throw new Error(result.message || 'Failed to create task status')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async updateTaskStatus({ commit }, input) {
      try {
        const response = await boardsApi.updateTaskStatus(input)
        const result = response.data.updateTaskStatus
        if (result.status === 'RECORD_UPDATED') {
          commit('UPDATE_STATUS', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to update task status')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async deleteTaskStatus({ commit }, { id, reassignStatusId }) {
      try {
        const response = await boardsApi.deleteTaskStatus(id, reassignStatusId)
        const result = response.data.deleteTaskStatus
        if (result.status === 'RECORD_DELETED') {
          commit('REMOVE_STATUS', id)
          return result
        }
        throw new Error(result.message || 'Failed to delete task status')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async createTask({ state, commit }, input) {
      try {
        commit('SET_PROCESSING', true)
        const response = await boardsApi.createTask(input)
        const result = response.data.createTask
        if (result.status === 'RECORD_INSERTED') {
          commit('ADD_TASK', result.data)

          if (state.temporaryAssetToken) {
            await boardsApi.attachTemporaryAsset({
              input: {
                taskUid: result.data.uuid,
                temporaryToken: state.temporaryAssetToken,
              },
            })
            commit('SET_TEMPORARY_ASSET_TOKEN', null)
          }
          return result
        }
        throw new Error(result.message || 'Failed to create task')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_PROCESSING', false)
      }
    },

    // Board members
    async addBoardMember({ commit }, { boardUid, memberUid }) {
      try {
        const response = await boardsApi.addBoardMember({ boardUid, memberUid })
        const result = response.data.addBoardMember
        if (result.status === 'success' && result.data) {
          // Update the current board with new members data
          commit('SET_CURRENT_BOARD', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to add board member')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async removeBoardMember({ commit }, { boardUid, memberUid }) {
      try {
        const response = await boardsApi.removeBoardMember({ boardUid, memberUid })
        const result = response.data.removeBoardMember
        if (result.status === 'success' && result.data) {
          // Update the current board with new members data
          commit('SET_CURRENT_BOARD', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to remove board member')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async fetchTask({ commit }, uuid) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const response = await boardsApi.fetchTask({ uuid })
        commit('SET_TASK', response.data.task)
        return response.data.task
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateTask({ commit }, { uuid, input }) {
      try {
        commit('SET_PROCESSING', true)
        const response = await boardsApi.updateTask({ uuid, input })
        const result = response.data.updateTask
        if (result.status === 'RECORD_UPDATED') {
          commit('UPDATE_TASK', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to update task')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_PROCESSING', false)
      }
    },

    async moveTask({ commit }, { taskUid, currentStatusId, targetStatusId }) {
      try {
        const data = {
          taskUid,
          statusId: targetStatusId,
        }
        commit('CHANGE_TASK_STATUS', { taskUid, currentStatusId, targetStatusId })
        const response = await boardsApi.moveTask(data)
        const result = response.data.moveTask
        if (result.status === 'RECORD_UPDATED') {
          commit('UPDATE_TASK', result.data)
          return result
        }
        throw new Error(result.message || 'Failed to move task')
      } catch (error) {
        commit('CHANGE_TASK_STATUS', { taskUid, targetStatusId: currentStatusId, currentStatusId: targetStatusId })
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async deleteTask({ commit }, uuid) {
      try {
        const response = await boardsApi.deleteTask(uuid)
        const result = response.data.deleteTask
        if (result.status === 'RECORD_DELETED') {
          commit('REMOVE_TASK', uuid)
          return result
        }
        throw new Error(result.message || 'Failed to delete task')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async fetchBoardLabels({ commit }, boardUid) {
      try {
        const response = await boardsApi.fetchBoardLabels(boardUid)
        const result = response.data.board.labels.data
        if (result) {
          commit('SET_BOARD_LABELS', result)
          return result
        }
        throw new Error('Failed to fetch board labels')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async createBoardLabel({ commit }, input) {
      try {
        const response = await boardsApi.createBoardLabel(input)
        const result = response.data.createBoardLabel
        if (result.status === 'RECORD_INSERTED') {
          if (this.state.currentBoard) {
            const updatedLabels = [...(this.state.currentBoard.labels || []), result.data]
            commit('SET_CURRENT_BOARD', { ...this.state.currentBoard, labels: updatedLabels })
          }
          return result
        }
        throw new Error(result.message || 'Failed to create board label')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async updateBoardLabel({ state, commit }, payload) {
      try {
        const response = await boardsApi.updateBoardLabel(payload)
        const result = response.data.updateBoardLabel
        if (result.status === 'RECORD_UPDATED') {
          if (state.currentBoard) {
            const updatedLabels = (state.currentBoard.labels.data || []).map(label => (label.uuid === payload.labelUid ? { ...payload.input, uuid: payload.labelUid } : label))
            commit('SET_CURRENT_BOARD', { ...state.currentBoard, labels: { data: updatedLabels } })
          }
          return result
        }
        throw new Error(result.message || 'Failed to update board label')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async deleteBoardLabel({ state, commit }, input) {
      try {
        const response = await boardsApi.deleteBoardLabel(input)
        const result = response.data.deleteBoardLabel
        if (result.status === 'RECORD_DELETED') {
          if (state.currentBoard) {
            const updatedLabels = (state.currentBoard.labels.data || []).filter(label => label.uuid !== input.labelUid)
            const updatedCurrentBoard = { ...state.currentBoard, labels: { data: updatedLabels } }
            commit('SET_CURRENT_BOARD', updatedCurrentBoard)
          }
          return result
        }
        throw new Error(result.message || 'Failed to delete board label')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async createBoardMilestone({ commit }, input) {
      try {
        const response = await boardsApi.createMilestone(input)
        const result = response.data.createMilestone
        if (result.status === 'RECORD_INSERTED') {
          if (this.state.currentBoard) {
            const updatedMilestones = [...(this.state.currentBoard.milestones || []), result.data]
            commit('SET_CURRENT_BOARD', { ...this.state.currentBoard, milestones: updatedMilestones })
          }
          return result
        }
        throw new Error(result.message || 'Failed to create board milestone')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async updateBoardMilestone({ state, commit }, payload) {
      try {
        const response = await boardsApi.updateMilestone(payload)
        const result = response.data.updateMilestone
        if (result.status === 'RECORD_UPDATED') {
          if (state.currentBoard) {
            const updatedMilestones = (state.currentBoard.milestones.data || []).map(milestone => (milestone.uuid === payload.uuid ? { ...payload.input, uuid: payload.uuid } : milestone))
            commit('SET_CURRENT_BOARD', { ...state.currentBoard, milestones: { data: updatedMilestones } })
          }
          return result
        }
        throw new Error(result.message || 'Failed to update board milestone')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async deleteBoardMilestone({ state, commit }, input) {
      try {
        const response = await boardsApi.deleteMilestone(input)
        const result = response.data.deleteMilestone
        if (result.status === 'RECORD_DELETED') {
          if (state.currentBoard) {
            const updatedMilestones = (state.currentBoard.milestones.data || []).filter(milestone => milestone.uuid !== input.uuid)
            const updatedCurrentBoard = { ...state.currentBoard, milestones: { data: updatedMilestones } }
            commit('SET_CURRENT_BOARD', updatedCurrentBoard)
          }
          return result
        }
        throw new Error(result.message || 'Failed to delete board milestone')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async fetchTasksByStatusKey({ state, commit }, {
      statusKey, assignees = [], labelUids = [], search = null,
    }) {
      try {
        commit('SET_STATUS_PAGES', {
          statusKey,
          data: {
            ...state.statusPages[statusKey],
            fetchingMoreTasks: true,
          },
        })

        const data = {
          boardUid: state.currentBoard.uuid,
          statusKeys: [statusKey],
          page: state.statusPages[statusKey].page + 1,
        }

        if (assignees.length) {
          if (assignees[0] === 'unassigned') {
            data.assignees = []
          } else {
            data.assignees = assignees
          }
        }

        if (labelUids.length) data.labelUids = labelUids
        if (search) data.search = search

        const response = await boardsApi.getTasksByStatusKey(data)
        commit('SET_CURRENT_BOARD', {
          ...state.currentBoard,
          statuses: state.currentBoard.statuses.map(status => {
            if (status.key === statusKey) {
              const currentTaskUids = status.tasks.data.map(task => task.uuid)
              const newTasks = response.data.board.tasks.data
                .filter(task => !currentTaskUids.includes(task.uuid))
              const allTasks = [...status.tasks.data, ...newTasks]

              return {
                ...status,
                tasks: {
                  ...status.tasks,
                  hasMorePages: response.data.board.tasks.hasMorePages,
                  data: [...allTasks],
                },
              }
            }
            return status
          }),
        })
        commit('SET_STATUS_PAGES', {
          statusKey,
          data: {
            page: state.statusPages[statusKey].page + 1,
            hasMorePages: response.data.board.tasks.hasMorePages,
            fetchingMoreTasks: false,
          },
        })
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    setTemporaryAssetToken({ commit }, token) {
      commit('SET_TEMPORARY_ASSET_TOKEN', token)
    },
    async addTaskComment({ state, commit }, input) {
      try {
        const response = await boardsApi.addTaskComment(input)
        const result = response.data.addTaskComment
        if (result.status === 'RECORD_INSERTED') {
          if (state.temporaryAssetToken) {
            await boardsApi.attachTemporaryCommentAsset({
              input: {
                commentUid: result.data.uuid,
                temporaryToken: state.temporaryAssetToken,
              },
            })
            commit('SET_TEMPORARY_ASSET_TOKEN', null)
          }

          commit('SET_TASK', {
            ...state.task,
            comments: [
              ...state.task.comments,
              result.data,
            ],
          })
          return result
        }
        throw new Error(result.message || 'Failed to add task comment')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    async updateTaskComment({ state, commit }, { commentUid, content }) {
      try {
        const response = await boardsApi.updateTaskComment({ commentUid, content })
        const result = response.data.updateTaskComment
        if (result.status === 'RECORD_UPDATED') {
          commit('SET_TASK', {
            ...state.task,
            comments: state.task.comments.map(comment => (comment.uuid === commentUid
              ? { ...comment, content, updatedAt: new Date().toISOString() }
              : comment)),
          })
          return result
        }
        throw new Error(result.message || 'Failed to edit task comment')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    async deleteTaskComment({ state, commit }, input) {
      try {
        const response = await boardsApi.deleteTaskComment(input)
        const result = response.data.deleteTaskComment
        if (result.status === 'RECORD_DELETED') {
          commit('SET_TASK', {
            ...state.task,
            comments: [...state.task.comments.filter(comment => comment.uuid !== input.commentUid)],
          })
          return result
        }
        throw new Error(result.message || 'Failed to delete task comment')
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },
    async getMemberPerformance({ commit }, {
      boardUid, memberUid, projectUid, timeRange,
    }) {
      commit('SET_LOADING', true)
      try {
        const response = await boardsApi.getMemberPerformance({
          projectUid,
          boardUid,
          memberUid,
          timeRange,
        })
        const result = response.data.memberPerformance
        commit('SET_CURRENT_BOARD_MEMBER_PERFORMANCE', result)
        return result
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchTeamPerformance({ commit }, {
      boardUid, projectUid, timeRange,
    }) {
      commit('SET_LOADING', true)
      try {
        const response = await boardsApi.getTeamPerformance({
          projectUid,
          boardUid,
          timeRange,
        })
        const { teamPerformance } = response.data
        commit('SET_CURRENT_BOARD_TEAM_PERFORMANCE', teamPerformance)
        return { teamPerformance }
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchTeamAndBoardPerformance({ commit }, {
      boardUid, projectUid, timeRange,
    }) {
      commit('SET_LOADING', true)
      try {
        const response = await boardsApi.getTeamAndBoardPerformance({
          projectUid,
          boardUid,
          timeRange,
        })
        const { teamPerformance, boardPerformance } = response.data
        commit('SET_CURRENT_BOARD_TEAM_PERFORMANCE', teamPerformance)
        commit('SET_CURRENT_BOARD_PERFORMANCE', boardPerformance?.taskTrend)
        return { teamPerformance, boardPerformance }
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    clearPerformanceStats({ commit }) {
      commit('SET_CURRENT_BOARD_MEMBER_PERFORMANCE', null)
      commit('SET_CURRENT_BOARD_TEAM_PERFORMANCE', null)
      commit('SET_CURRENT_BOARD_PERFORMANCE', null)
    },
  },
}
